import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Settings, Smartphone, Wifi, Shield, MapPin, Clock, AlertTriangle, PlayCircle, CloudOff, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useLanguage } from '../LanguageProvider';

type Lang = 'tr' | 'en';
type Localized<T> = Record<Lang, T>;

type Specification = {
  label: Localized<string>;
  value: Localized<string>;
};

type Project = {
  title: Localized<string>;
  category: Localized<string>;
  description: Localized<string>;
  image: string;
  features: Localized<string[]>;
  specifications: Specification[];
  useCases: Localized<string[]>;
  technologies: string[];
  warnings?: Localized<string[]>;
  androidAppUrl?: string;
  detailedUseCases?: {
    title: Localized<string>;
    description: Localized<string>;
    points?: Localized<string[]>;
  }[];
};

export function ProjectDetail() {
  const { slug } = useParams();
  const { language } = useLanguage();
  const [emergencyMode, setEmergencyMode] = useState(false);

  // ShalterOn Simülasyon State'leri
  const [relayStates, setRelayStates] = useState({
    relay1: false, // Sera Aydınlatma
    relay2: false, // Su Pompası 1
    relay3: false, // Su Pompası 2
    relay4: false, // Su Pompası 3
    relay5: false, // Havalandırma Fanı
    relay6: false, // Elektrikli Kapı
  });

  const toggleRelay = (relayKey: keyof typeof relayStates) => {
    setRelayStates(prev => ({ ...prev, [relayKey]: !prev[relayKey] }));
  };

  const getLocalized = <T,>(value: Localized<T>): T => value[language] ?? value.tr;

  const texts = {
    tr: {
      backToProducts: 'Ürünlere Dön',
      exploreMobileApp: 'Mobil Uygulamayı Keşfedin',
      features: 'Özellikler',
      technicalSpecs: 'Teknik Özellikler',
      connection: 'Bağlantı',
      location: 'Konum',
      sensors: 'Sensörler',
      temperatureHumidity: 'Sıcaklık ve Nem',
      battery: 'Batarya',
      general: 'Genel',
      packageContents: 'Kutu İçeriği',
      warningsTitle: 'Kullanımdan Önce Dikkat Edilmesi Gerekenler',
      useCases: 'Kullanım Alanları',
      productDocumentation: 'Ürün Dokümantasyonu',
      viewPdf: 'PDF\'i Görüntüle',
      technologies: 'Kullanılan Teknolojiler',
      packageItem1: 'ShalterOn Kontrol Cihazı',
      packageItem2: '3M Anten',
      packageItem3: '12VDC 1A Adaptör',
      quantity: 'adet',
      ctaTitle: 'Bu Proje Hakkında Daha Fazla Bilgi Almak İster misiniz?',
      ctaDescription: 'Teknik detaylar, fiyat bilgisi ve kurulum süreci hakkında uzman ekibimizden bilgi alın.',
      visitWebsite: 'Frigolive Web Sitesini Ziyaret Edin',
      contactUs: 'İletişime Geçin',
      viewOtherProjects: 'Diğer Projeleri İncele',
      projectNotFound: 'Proje Bulunamadı',
      projectNotFoundDesc: 'Aradığınız proje mevcut değil.',
      testEmergencyMode: 'Acil Durum Modunu Test Et',
      emergencyModeActive: 'Acil Durum Modu Aktif - Tüm Kapılar Açık',
      emergencyModeDesc: 'Sistem yangın alarmı algıladı. Tahliye için bariyerler kaldırıldı.'
    },
    en: {
      backToProducts: 'Back to Products',
      exploreMobileApp: 'Explore Mobile App',
      features: 'Features',
      technicalSpecs: 'Technical Specifications',
      connection: 'Connection',
      location: 'Location',
      sensors: 'Sensors',
      temperatureHumidity: 'Temperature and Humidity',
      battery: 'Battery',
      general: 'General',
      packageContents: 'Package Contents',
      warningsTitle: 'Important Notes Before Use',
      useCases: 'Use Cases',
      productDocumentation: 'Product Documentation',
      viewPdf: 'View PDF',
      technologies: 'Technologies Used',
      packageItem1: 'ShalterOn Control Device',
      packageItem2: '3M Antenna',
      packageItem3: '12VDC 1A Adapter',
      quantity: 'unit',
      ctaTitle: 'Would You Like More Information About This Project?',
      ctaDescription: 'Get information about technical details, pricing, and installation process from our expert team.',
      visitWebsite: 'Visit Frigolive Website',
      contactUs: 'Contact Us',
      viewOtherProjects: 'View Other Projects',
      projectNotFound: 'Project Not Found',
      projectNotFoundDesc: 'The project you are looking for does not exist.',
      testEmergencyMode: 'Test Emergency Mode',
      emergencyModeActive: 'Emergency Mode Active - All Doors Open',
      emergencyModeDesc: 'System detected fire alarm. Barriers lifted for evacuation.'
    }
  } as const;

  const t = texts[language] ?? texts.tr;

  const projects: Record<string, Project> = {
    'frigolive': {
      title: {
        tr: 'Frigolive F-007',
        en: 'Frigolive F-007'
      },
      category: {
        tr: 'IoT & Sensörler',
        en: 'IoT & Sensors'
      },
      description: {
        tr: 'Araç ve lojistik sektörü için geliştirilmiş, GSM tabanlı canlı konum, sıcaklık, nem, ışık ve darbe verilerini izleyerek lojistik ve hassas ürün transferi süresince ürün bütünlüğünü ve güvenliğini garanti altına alın.',
        en: 'Developed for the automotive and logistics sector, ensure product integrity and safety during logistics and sensitive product transfers by monitoring real-time location, temperature, humidity, light and shock data with GSM-based system.'
      },
      image: '/frigolive-2.png',
      features: {
        tr: [
          'Canlı Sıcaklık Takibi',
          'Hücresel Bağlantı',
          'Canlı Konum Takibi',
          'Web Tabanlı Raporlama',
          'Uzun Batarya Ömrü',
          'Mobil Uygulama Desteği',
          'Su Geçirmez Tasarım',
          'Kolay Kullanım'
        ],
        en: [
          'Live Temperature Tracking',
          'Cellular Connectivity',
          'Live Location Tracking',
          'Web-Based Reporting',
          'Long Battery Life',
          'Mobile App Support',
          'Waterproof Design',
          'Easy to Use'
        ]
      },
      specifications: [
        // Bağlantı
        {
          label: { tr: 'İletişim Aralığı', en: 'Communication Interval' },
          value: { tr: '5 dakika - 24 saat (seçilebilir aralık)', en: '5 minutes - 24 hours (selectable interval)' }
        },

        // Konum
        {
          label: { tr: 'Konumlama Yöntemi', en: 'Positioning Method' },
          value: { tr: 'GPS, WiFi, Hücresel Üçgenleme', en: 'GPS, WiFi, Cellular Triangulation' }
        },
        {
          label: { tr: 'Konum Doğruluğu', en: 'Location Accuracy' },
          value: { tr: 'GPS: 10 m, WiFi: 10 m, Hücresel: 500 m', en: 'GPS: 10 m, WiFi: 10 m, Cellular: 500 m' }
        },

        // Sensörler
        {
          label: { tr: 'Sensörler', en: 'Sensors' },
          value: { tr: 'Sıcaklık, Nem, Işık, Darbe', en: 'Temperature, Humidity, Light, Shock' }
        },
        {
          label: { tr: 'Ölçüm Kapasitesi', en: 'Measurement Capacity' },
          value: { tr: '30.000 kayda kadar', en: 'Up to 30,000 records' }
        },
        {
          label: { tr: 'Kalibrasyon', en: 'Calibration' },
          value: { tr: 'ISO/IEC 17025 akredite, NIST izlenebilir', en: 'ISO/IEC 17025 accredited, NIST traceable' }
        },

        // Sıcaklık ve Nem
        {
          label: { tr: 'Çalışma Sıcaklığı Aralığı', en: 'Operating Temperature Range' },
          value: { tr: '-25°C ile +60°C', en: '-25°C to +60°C' }
        },
        {
          label: { tr: 'Nem Aralığı', en: 'Humidity Range' },
          value: { tr: '0%-100% RH', en: '0%-100% RH' }
        },
        {
          label: { tr: 'Sıcaklık Hassasiyeti', en: 'Temperature Accuracy' },
          value: { tr: '±0.1°C', en: '±0.1°C' }
        },
        {
          label: { tr: 'Nem Hassasiyeti', en: 'Humidity Accuracy' },
          value: { tr: '±2% RH', en: '±2% RH' }
        },
        {
          label: { tr: 'Sıcaklık Sürüklenmesi', en: 'Temperature Drift' },
          value: { tr: 'Yılda <0.03°C', en: '<0.03°C per year' }
        },
        {
          label: { tr: 'Nem Sürüklenmesi', en: 'Humidity Drift' },
          value: { tr: 'Yılda %0.25', en: '0.25% per year' }
        },
        {
          label: { tr: 'Düşük Sıcaklık Desteği', en: 'Low Temperature Support' },
          value: { tr: '-30°C\'a kadar çalışma için tasarlanmıştır', en: 'Designed for operation down to -30°C' }
        },

        // Batarya
        {
          label: { tr: 'Batarya Kapasitesi', en: 'Battery Capacity' },
          value: { tr: 'Li-ion 2800mAh / 10.08 Wh', en: 'Li-ion 2800mAh / 10.08 Wh' }
        },
        {
          label: { tr: 'Batarya Ömrü', en: 'Battery Life' },
          value: { tr: '30+ Gün (60 dk iletim), 150+ Gün (24 Saat iletim)', en: '30+ Days (60 min interval), 150+ Days (24 hr interval)' }
        },

        // Genel
        {
          label: { tr: 'Boyutlar', en: 'Dimensions' },
          value: { tr: '108 x 62 x 24 mm', en: '108 x 62 x 24 mm' }
        },
        {
          label: { tr: 'Ağırlık', en: 'Weight' },
          value: { tr: '110 gram', en: '110 grams' }
        },
        {
          label: { tr: 'Koruma', en: 'Protection' },
          value: { tr: 'IP67 (su ve toz geçirmez)', en: 'IP67 (waterproof and dustproof)' }
        },
        {
          label: { tr: 'Raf Ömrü', en: 'Shelf Life' },
          value: { tr: '1 yıl', en: '1 year' }
        }
      ],
      useCases: {
        tr: [
          'İlaç ve aşı taşımacılığı',
          'Gıda lojistiği',
          'Laboratuvar örnekleri',
          'Soğuk zincir yönetimi',
          'Endüstriyel sıcaklık takibi',
          'Sera ve tarım ürünleri'
        ],
        en: [
          'Pharmaceutical and vaccine transport',
          'Food logistics',
          'Laboratory samples',
          'Cold chain management',
          'Industrial temperature tracking',
          'Greenhouse and agricultural products'
        ]
      },
      technologies: ['GSM/GPRS', 'Temperature Sensors', 'Cloud Platform', 'Web Dashboard']
    },
    'shalteron': {
      title: {
        tr: 'Mesafeleri Ortadan Kaldırın: Endüstriyel GSM Kontrol Ünitesi',
        en: 'Eliminate Distances: Industrial GSM Control Unit'
      },
      category: {
        tr: 'Otomasyon',
        en: 'Automation'
      },
      description: {
        tr: 'Wi-Fi veya kablolu internete ihtiyaç duymadan, GSM altyapısı ile dünyanın her yerinden cihazlarınızı yönetin. 6 Röle Çıkışı, 4 Dijital Giriş ile tam hakimiyet.',
        en: 'Manage your devices from anywhere in the world with GSM infrastructure, without the need for Wi-Fi or wired internet. Full control with 6 Relay Outputs and 4 Digital Inputs.'
      },
      image: '/gsm_role.png',
      features: {
        tr: [
          '6 adet röle çıkışı (10A/250V)',
          '4 adet dijital input',
          'Mobil uygulama kontrolü',
          'Zamanlama ve programlama',
          'M2M SIM Kart',
          'Durum raporlama'
        ],
        en: [
          '6 relay outputs (10A/250V)',
          '4 digital inputs',
          'Mobile app control',
          'Scheduling and programming',
          'M2M SIM Card',
          'Status reporting'
        ]
      },
      specifications: [
        {
          label: { tr: 'Bağlanabilir Cihaz Sayısı', en: 'Connectable Device Count' },
          value: { tr: '6 adet - NO/COM/NC', en: '6 units - NO/COM/NC' }
        },
        {
          label: { tr: 'Kontrol Kapasitesi', en: 'Control Capacity' },
          value: { tr: '10A 250VAC | 10A 30VDC', en: '10A 250VAC | 10A 30VDC' }
        },
        {
          label: { tr: 'Digital Input', en: 'Digital Input' },
          value: { tr: '4 adet (Enerji vermeyin!)', en: '4 units (Do not apply power!)' }
        },
        {
          label: { tr: 'Bağlantı', en: 'Connection' },
          value: { tr: 'M2M SIM Kart | Hücresel Veri 10MB/Ay', en: 'M2M SIM Card | Cellular Data 10MB/Month' }
        },
        {
          label: { tr: 'Anten', en: 'Antenna' },
          value: { tr: '3M', en: '3M' }
        },
        {
          label: { tr: 'Güç Kaynağı', en: 'Power Supply' },
          value: { tr: '12V DC 1A Adaptör', en: '12V DC 1A Adapter' }
        },
        {
          label: { tr: 'Boyutlar', en: 'Dimensions' },
          value: { tr: 'RT 207 Ray Tipi Kutu', en: 'RT 207 DIN Rail Box' }
        },
        {
          label: { tr: 'Koruma', en: 'Protection' },
          value: { tr: 'IP54', en: 'IP54' }
        },
        {
          label: { tr: 'Buzzer', en: 'Buzzer' },
          value: { tr: '1 adet', en: '1 unit' }
        }
      ],
      useCases: {
        tr: [
          'Bahçe sulama sistemleri',
          'Sera otomasyon',
          'Endüstriyel ekipman kontrolü',
          'Aydınlatma kontrolü',
          'Motor kontrol sistemleri',
          'Alarm sistemleri'
        ],
        en: [
          'Garden irrigation systems',
          'Greenhouse automation',
          'Industrial equipment control',
          'Lighting control',
          'Motor control systems',
          'Alarm systems'
        ]
      },
      technologies: ['GSM/GPRS', 'IoT', 'Mobile App', 'SMS Gateway', 'M2M SIM Card'],
      warnings: {
        tr: [
          '10 Amperden yüksek güçler için kontaktör kullanın.',
          'Okuma girişlerine 12V\'dan fazla enerji vermeyin!',
          'Cihaz girişleri kuru kontaktır. Enerji girişi yapmayın!',
          'Cihaz kurulumu için tecrübeli bir teknik servisten destek alın.'
        ],
        en: [
          'Use a contactor for powers higher than 10 Amps.',
          'Do not apply more than 12V to the input terminals!',
          'Device inputs are dry contacts. Do not apply power!',
          'Get support from an experienced technical service for device installation.'
        ]
      }
    },
    'akilli-gecis-sistemi': {
      title: {
        tr: 'Akıllı Geçiş Sistemi',
        en: 'Smart Access System'
      },
      category: {
        tr: 'Güvenlik Sistemleri',
        en: 'Security Systems'
      },
      description: {
        tr: 'QR kod ve mobil uygulama entegrasyonlu modern erişim kontrol sistemi. Okul, ofis ve fabrika girişleri için yoklama ve detaylı raporlama özellikleri sunar.',
        en: 'Modern access control system with QR code and mobile app integration. Offers attendance and detailed reporting features for school, office and factory entrances.'
      },
      image: '/kgs_1.png',
      features: {
        tr: [
          'Dinamik QR kod ile giriş',
          'RFID kart desteği',
          'Mobil uygulama entegrasyonu',
          'Yoklama sistemi',
          'Detaylı raporlama',
          'Zaman dilimi kontrolü'
        ],
        en: [
          'Dynamic QR code entry',
          'RFID card support',
          'Mobile app integration',
          'Attendance system',
          'Detailed reporting',
          'Time zone control'
        ]
      },
      specifications: [
        {
          label: { tr: 'Kullanıcı Kapasitesi', en: 'User Capacity' },
          value: { tr: '20.000 kişi', en: '20,000 people' }
        },
        {
          label: { tr: 'Kayıt Kapasitesi', en: 'Record Capacity' },
          value: { tr: '100.000 kayıt', en: '100,000 records' }
        },
        {
          label: { tr: 'Tanımlama Yöntemleri', en: 'Identification Methods' },
          value: { tr: 'Proximity kart, Mifare kart, Mobil barkod', en: 'Proximity card, Mifare card, Mobile barcode' }
        },
        {
          label: { tr: 'Kart Tipi', en: 'Card Type' },
          value: { tr: 'RFID 13.56MHz, QR Code', en: 'RFID 13.56MHz, QR Code' }
        },
        {
          label: { tr: 'Okuma Mesafesi', en: 'Reading Distance' },
          value: { tr: '5-10 cm (kart) , barkod okuyucu destekli', en: '5-10 cm (card), barcode reader supported' }
        },
        {
          label: { tr: 'Ekran', en: 'Display' },
          value: { tr: '2.4" Renkli TFT Ekran', en: '2.4" Color TFT Display' }
        },
        {
          label: { tr: 'Bağlantı', en: 'Connection' },
          value: { tr: 'Ethernet / WiFi / RS-485 / USB', en: 'Ethernet / WiFi / RS-485 / USB' }
        },
        {
          label: { tr: 'Güç', en: 'Power' },
          value: { tr: '12V DC 500mA', en: '12V DC 500mA' }
        },
        {
          label: { tr: 'Boyutlar', en: 'Dimensions' },
          value: { tr: '200x150x40 mm', en: '200x150x40 mm' }
        },
        {
          label: { tr: 'Ek Fonksiyonlar', en: 'Additional Functions' },
          value: { tr: 'Anti-pass Back, Zaman Dilimi Tanımlama, Uyku Modu', en: 'Anti-pass Back, Time Zone Definition, Sleep Mode' }
        }
      ],
      useCases: {
        tr: [
          'Okul Giriş Kontrolü',
          'Ofis Personel Takibi',
          'Fabrika Vardiya Kontrolü',
          'Etkinlik Katılımcı Takibi',
          'Kütüphane Geçiş Sistemi',
          'Hastane Ziyaretçi Sistemi'
        ],
        en: [
          'School Access Control',
          'Office Personnel Tracking',
          'Factory Shift Control',
          'Event Participant Tracking',
          'Library Access System',
          'Hospital Visitor System'
        ]
      },
      detailedUseCases: [
        {
          title: {
            tr: 'Turnike Geçiş Sistemleri',
            en: 'Turnstile Access Systems'
          },
          description: {
            tr: 'İlimera PDKS cihazı, personel ve öğrencilerin turnikeden güvenli ve kontrollü geçişini sağlar. Kart veya mobil barkod okutulduğunda turnike otomatik açılır, yoğun saatlerde bile hızlı ve düzenli geçiş imkânı sunar. Tüm geçişler anlık olarak sisteme kaydedilir ve istenildiğinde detaylı rapor alınabilir. Bu sayede kurumlarda güvenlik ve devam kontrolü kolayca sağlanır.',
            en: 'İlimera PDKS device ensures safe and controlled passage of personnel and students through turnstiles. The turnstile opens automatically when a card or mobile barcode is scanned, providing fast and orderly passage even during busy hours. All passages are instantly recorded in the system and detailed reports can be obtained when needed. This way, security and attendance control in institutions is easily ensured.'
          },
          points: {
            tr: [
              'Personel ve öğrencilerin turnikeden güvenli ve kontrollü geçişini sağlar.',
              'Kart veya mobil QR kod ile geçiş imkânı sunar.',
              'Yoğun saatlerde bile hızlı ve düzenli geçiş sağlar.',
              'Tüm geçişler anlık olarak sisteme kaydedilir.',
              'Yetkisiz girişler engellenir.',
              'Detaylı rapor alınabilir.',
              'Kurumlarda güvenlik ve devam kontrolünü kolaylaştırır.'
            ],
            en: [
              'Ensures secure and controlled passage for staff and students through turnstiles.',
              'Provides access using cards or mobile QR codes.',
              'Ensures fast and orderly passage even during peak hours.',
              'All passages are instantly recorded in the system.',
              'Unauthorized entries are prevented.',
              'Detailed reports can be obtained.',
              'Facilitates security and attendance control in institutions.'
            ]
          }
        },
        {
          title: {
            tr: 'Kapı Geçiş Sistemleri',
            en: 'Door Access Systems'
          },
          description: {
            tr: 'İlimera PDKS cihazı, kurumlarda kapı giriş-çıkış noktalarında güvenli ve kontrollü erişim sağlar. Yetkilendirilmiş personel veya öğrenciler kart veya mobil barkod ile kolayca giriş yapabilir. Yetkisiz erişim denemeleri ise cihaz tarafından engellenir; sesli ve görsel uyarılar devreye girer ve sistem yöneticisine bildirim gönderilir. Tüm giriş-çıkışlar anlık kaydedilir ve detaylı raporlar oluşturularak kurum güvenliği ve takip süreçleri güçlendirilir.',
            en: 'İlimera PDKS device provides secure and controlled access at door entry-exit points in institutions. Authorized personnel or students can easily enter with card or mobile barcode. Unauthorized access attempts are blocked by the device; audible and visual alerts are activated and notifications are sent to the system administrator. All entries and exits are instantly recorded and detailed reports are created to strengthen institutional security and tracking processes.'
          },
          points: {
            tr: [
              'Kurumlarda kapı giriş-çıkış noktalarında güvenli ve kontrollü erişim sağlar.',
              'Yetkilendirilmiş personel veya öğrenciler kart veya mobil barkod ile giriş yapabilir.',
              'Yetkisiz erişim denemeleri cihaz tarafından engellenir.',
              'Sesli ve görsel uyarılar devreye girer.',
              'Sistem yöneticisine bildirim gönderilir.',
              'Tüm giriş-çıkışlar anlık olarak sisteme kaydedilir.',
              'Detaylı raporlar oluşturularak güvenlik ve takip süreçleri güçlendirilir.'
            ],
            en: [
              'Ensures secure and controlled access at door entry-exit points in institutions.',
              'Authorized personnel or students can enter with a card or mobile barcode.',
              'Unauthorized access attempts are blocked by the device.',
              'Audible and visual warnings are activated.',
              'Notifications are sent to the system administrator.',
              'All entries and exits are instantly recorded in the system.',
              'Detailed reports are generated, strengthening security and monitoring processes.'
            ]
          }
        },
        {
          title: {
            tr: 'Yemekhane Kullanımı',
            en: 'Cafeteria Usage'
          },
          description: {
            tr: 'İlimera PDKS cihazı, yemekhane girişlerinde personel ve öğrencilerin yemek haklarını hızlı ve güvenli şekilde kontrol eder. Kart veya mobil barkod ile geçiş yapılabilir; sistem yalnızca yetkili kişilerin girişine izin verir. Bu sayede yemek israfı ve yetkisiz kullanım önlenir. Tüm girişler anlık kaydedilir ve yöneticiler detaylı raporlara ulaşabilir. Yemekhane kullanımı böylece düzenli, denetlenebilir ve adil şekilde yönetilir.',
            en: 'İlimera PDKS device quickly and securely checks meal rights of personnel and students at cafeteria entrances. Access can be made with card or mobile barcode; the system only allows entry of authorized persons. This prevents food waste and unauthorized use. All entries are instantly recorded and administrators can access detailed reports. Cafeteria usage is thus managed in an organized, auditable and fair manner.'
          },
          points: {
            tr: [
              'Yemekhane girişlerinde personel ve öğrencilerin yemek haklarını hızlı ve güvenli şekilde kontrol eder.',
              'Kart veya mobil barkod ile geçiş imkânı sunar.',
              'Yalnızca yetkili kişilerin girişine izin verir.',
              'Yemek israfı ve yetkisiz kullanımın önüne geçer.',
              'Tüm girişler anlık olarak sisteme kaydedilir.',
              'Yöneticiler detaylı raporlara ulaşabilir.',
              'Yemekhane kullanımı düzenli, denetlenebilir ve adil şekilde yönetilir.'
            ],
            en: [
              'Quickly and securely checks meal rights of personnel and students at cafeteria entrances.',
              'Provides access using cards or mobile barcodes.',
              'Only allows entry of authorized persons.',
              'Prevents food waste and unauthorized use.',
              'All entries are instantly recorded in the system.',
              'Administrators can access detailed reports.',
              'Cafeteria usage is managed in an organized, auditable and fair manner.'
            ]
          }
        },
        {
          title: {
            tr: 'Toplantı Odası Geçiş Kontrolü',
            en: 'Meeting Room Access Control'
          },
          description: {
            tr: 'İlimera PDKS cihazı, toplantı odalarına yalnızca yetkili personelin girişini sağlar. Kart veya mobil barkod ile erişim sağlanır, yetkisiz giriş denemeleri engellenir ve gerekirse alarm veya uyarı gönderilir. Tüm giriş-çıkışlar kaydedilir; hangi personelin hangi saatlerde odayı kullandığı raporlanabilir. Böylece güvenlik artırılır ve kurum içi kaynaklar verimli şekilde yönetilir.',
            en: 'İlimera PDKS device ensures that only authorized personnel can enter meeting rooms. Access is provided with card or mobile barcode, unauthorized entry attempts are blocked and alarms or warnings are sent if necessary. All entries and exits are recorded; which personnel used the room at what times can be reported. Thus, security is increased and internal resources are managed efficiently.'
          },
          points: {
            tr: [
              'Toplantı odalarına yalnızca yetkili personelin girişini sağlar.',
              'Kart veya mobil barkod ile erişim imkânı sunar.',
              'Yetkisiz giriş denemeleri engellenir.',
              'Gerekirse alarm veya uyarı gönderilir.',
              'Tüm giriş-çıkışlar kaydedilir.',
              'Hangi personelin hangi saatlerde odayı kullandığı raporlanabilir.',
              'Güvenlik artırılır ve kurum içi kaynaklar verimli şekilde yönetilir.'
            ],
            en: [
              'Ensures that only authorized personnel can enter meeting rooms.',
              'Provides access using cards or mobile barcodes.',
              'Unauthorized entry attempts are blocked.',
              'Alarms or warnings are sent if necessary.',
              'All entries and exits are recorded.',
              'Which personnel used the room at what times can be reported.',
              'Security is increased and internal resources are managed efficiently.'
            ]
          }
        },
        {
          title: {
            tr: 'Kütüphane Geçiş Kontrolü',
            en: 'Library Access Control'
          },
          description: {
            tr: 'İlimera PDKS cihazı, kütüphane girişlerinde üyelerin güvenli ve düzenli erişimini sağlar. Kart veya mobil barkod ile giriş yapılabilir; yalnızca kayıtlı kullanıcılar erişebilir, yetkisiz girişler engellenir. Kapasite sınırlaması sayesinde kütüphanedeki kişi sayısı kontrol edilir. Rezervasyon özelliği ile önceden giriş hakkı tanımlanabilir ve kullanım verimliliği artar. Tüm giriş-çıkışlar kaydedilir ve yöneticilere raporlama imkânı sunulur.',
            en: 'İlimera PDKS device ensures safe and orderly access of members at library entrances. Entry can be made with card or mobile barcode; only registered users can access, unauthorized entries are blocked. The number of people in the library is controlled through capacity limitation. Entry rights can be defined in advance with the reservation feature and usage efficiency increases. All entries and exits are recorded and reporting capability is provided to administrators.'
          },
          points: {
            tr: [
              'Kütüphane girişlerinde üyelerin güvenli ve düzenli erişimini sağlar.',
              'Kart veya mobil barkod ile giriş imkânı sunar.',
              'Yalnızca kayıtlı kullanıcılar erişebilir; yetkisiz girişler engellenir.',
              'Kapasite sınırlaması ile kütüphanedeki kişi sayısı kontrol edilir.',
              'Rezervasyon özelliği ile önceden giriş hakkı tanımlanabilir, kullanım verimliliği artar.',
              'Tüm giriş-çıkışlar kaydedilir.',
              'Yöneticilere raporlama imkânı sunar.'
            ],
            en: [
              'Ensures safe and orderly access of members at library entrances.',
              'Provides entry using cards or mobile barcodes.',
              'Only registered users can access; unauthorized entries are blocked.',
              'The number of people in the library is controlled through capacity limitation.',
              'Entry rights can be defined in advance with the reservation feature, increasing usage efficiency.',
              'All entries and exits are recorded.',
              'Provides reporting capability to administrators.'
            ]
          }
        },
        {
          title: {
            tr: 'Etkinlik Geçiş Kontrolü',
            en: 'Event Access Control'
          },
          description: {
            tr: 'İlimera PDKS cihazı, konser, fuar, seminer ve benzeri etkinliklerde hızlı ve güvenli giriş sağlar. Katılımcılar bilet veya mobil QR kod ile giriş yapabilir; uzun kuyruklar önlenir ve düzenli geçiş sağlanır. Tüm girişler anlık kaydedilir, katılımcı sayısı takip edilir ve kapasite kontrolü yapılır. Yetkisiz girişler engellenerek güvenlik üst seviyede tutulur.',
            en: 'İlimera PDKS device provides fast and secure entry at concerts, fairs, seminars and similar events. Participants can enter with tickets or mobile QR codes; long queues are prevented and orderly passage is ensured. All entries are instantly recorded, participant numbers are tracked and capacity control is performed. Security is kept at the highest level by blocking unauthorized entries.'
          },
          points: {
            tr: [
              'Konser, fuar, seminer ve benzeri etkinliklerde hızlı ve güvenli giriş sağlar.',
              'Katılımcılar bilet veya mobil QR kod ile giriş yapabilir.',
              'Uzun kuyruklar önlenir ve düzenli geçiş sağlanır.',
              'Tüm girişler anlık kaydedilir.',
              'Katılımcı sayısı takip edilir ve kapasite kontrolü yapılır.',
              'Yetkisiz girişler engellenerek güvenlik üst seviyede tutulur.'
            ],
            en: [
              'Provides fast and secure entry at concerts, fairs, seminars and similar events.',
              'Participants can enter with tickets or mobile QR codes.',
              'Long queues are prevented and orderly passage is ensured.',
              'All entries are instantly recorded.',
              'Participant numbers are tracked and capacity control is performed.',
              'Security is kept at the highest level by blocking unauthorized entries.'
            ]
          }
        },
        {
          title: {
            tr: 'Hızlı Kasa Geçiş Kontrolü',
            en: 'Fast Checkout Access Control'
          },
          description: {
            tr: 'İlimera PDKS cihazı, market, mağaza ve self-servis alanlarında müşterilerin kasa işlemlerini hızlı ve güvenli şekilde tamamlamasını sağlar. Alışveriş fişi veya mobil QR kod ile geçiş yapılabilir; yoğun saatlerde kuyruklar önlenir ve müşteri memnuniyeti artar. Tüm geçişler kaydedilir, güvenlik ve satış takibi kolaylaşır. Günlük, haftalık veya aylık raporlarla işlem yoğunluğu analiz edilebilir.',
            en: 'İlimera PDKS device enables customers to complete checkout transactions quickly and securely in markets, stores and self-service areas. Access can be made with shopping receipt or mobile QR code; queues are prevented during busy hours and customer satisfaction increases. All passages are recorded, making security and sales tracking easier. Transaction intensity can be analyzed with daily, weekly or monthly reports.'
          },
          points: {
            tr: [
              'Market, mağaza ve self-servis alanlarında müşterilerin kasa işlemlerini hızlı ve güvenli şekilde tamamlamasını sağlar.',
              'Alışveriş fişi veya mobil QR kod ile geçiş yapılabilir.',
              'Yoğun saatlerde kuyruklar önlenir ve müşteri memnuniyeti artar.',
              'Tüm geçişler kaydedilir.',
              'Güvenlik ve satış takibi kolaylaşır.',
              'Günlük, haftalık veya aylık raporlarla işlem yoğunluğu analiz edilebilir.'
            ],
            en: [
              'Enables customers to complete checkout transactions quickly and securely in markets, stores and self-service areas.',
              'Access can be made with shopping receipt or mobile QR code.',
              'Queues are prevented during busy hours and customer satisfaction increases.',
              'All passages are recorded.',
              'Security and sales tracking become easier.',
              'Transaction intensity can be analyzed with daily, weekly or monthly reports.'
            ]
          }
        }
      ],
      technologies: ['RFID', 'QR Code', 'TCP/IP', 'Mobile App', 'Cloud Database']
    },
    'depo-takip-sistemi': {
      title: {
        tr: 'Depo Takip Sistemi',
        en: 'Warehouse Tracking System'
      },
      category: {
        tr: 'Lojistik Çözümleri',
        en: 'Logistics Solutions'
      },
      description: {
        tr: 'Forklift ve depo operasyonları için geliştirilmiş anlık takip sistemi. Sensör tabanlı konum belirleme ve kullanıcı yetkilendirme sistemi ile depo verimliliğini artırır.',
        en: 'Real-time tracking system developed for forklift and warehouse operations. Increases warehouse efficiency with sensor-based location detection and user authorization system.'
      },
      image: 'https://images.unsplash.com/photo-1672552226380-486fe900b322?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBmb3JrbGlmdCUyMHRyYWNraW5nfGVufDF8fHx8MTc1Njg4MzQwOHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      features: {
        tr: [
          'Anlık Forklift Konumu',
          'Çalışma Süresi Takibi',
          'Kullanıcı Kimlik Doğrulama',
          'Rota Optimizasyonu',
          'Performans Analizi',
          'Güvenlik Alarm Sistemi'
        ],
        en: [
          'Real-time Forklift Location',
          'Working Time Tracking',
          'User Authentication',
          'Route Optimization',
          'Performance Analysis',
          'Security Alarm System'
        ]
      },
      specifications: [
        {
          label: { tr: 'Konum Hassasiyeti', en: 'Location Accuracy' },
          value: { tr: '±1 metre', en: '±1 meter' }
        },
        {
          label: { tr: 'Güncelleme Sıklığı', en: 'Update Frequency' },
          value: { tr: '1 saniye', en: '1 second' }
        },
        {
          label: { tr: 'Bağlantı', en: 'Connection' },
          value: { tr: 'Wi-Fi, Bluetooth', en: 'Wi-Fi, Bluetooth' }
        },
        {
          label: { tr: 'Batarya Ömrü', en: 'Battery Life' },
          value: { tr: '8-12 saat', en: '8-12 hours' }
        },
        {
          label: { tr: 'Çalışma Sıcaklığı', en: 'Operating Temperature' },
          value: { tr: '-20°C ile +60°C', en: '-20°C to +60°C' }
        },
        {
          label: { tr: 'Koruma', en: 'Protection' },
          value: { tr: 'IP67', en: 'IP67' }
        }
      ],
      useCases: {
        tr: [
          'Depo Forklift Takibi',
          'Operasyon Verimliliği',
          'Güvenlik Monitoring',
          'Maliyet Optimizasyonu',
          'İş Sağlığı ve Güvenliği'
        ],
        en: [
          'Warehouse Forklift Tracking',
          'Operational Efficiency',
          'Security Monitoring',
          'Cost Optimization',
          'Occupational Health and Safety'
        ]
      },
      technologies: ['GPS/GNSS', 'Wi-Fi', 'IoT Sensors', 'Real-time Analytics']
    },
    'sanal-capa-projesi': {
      title: {
        tr: 'Sanal Çapa Projesi',
        en: 'Virtual Anchor Project'
      },
      category: {
        tr: 'Denizcilik Teknolojisi',
        en: 'Maritime Technology'
      },
      description: {
        tr: 'Denizcilik sektörü için geliştirilen innovatif çözüm. Fiziksel çapaya ihtiyaç olmadan teknenin GPS teknolojisi ile sabit konumda kalmasını sağlar.',
        en: 'Innovative solution developed for the maritime industry. Keeps the boat in a fixed position using GPS technology without the need for a physical anchor.'
      },
      image: 'https://images.unsplash.com/photo-1738339439832-860548f03cc4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxib2F0JTIwYW5jaG9yJTIwbWFyaW5lJTIwdGVjaG5vbG9neXxlbnwxfHx8fDE3NTY4ODM0MDh8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      features: {
        tr: [
          'GPS Tabanlı Sabit Durma',
          'Otomatik Konum Düzeltme',
          'Mobil Uygulama Kontrolü',
          'Kumanda ile Yön Kontrolü',
          'Planlı Rota Sistemi',
          'Acil Durum Modu'
        ],
        en: [
          'GPS-Based Stationary Positioning',
          'Automatic Position Correction',
          'Mobile App Control',
          'Remote Direction Control',
          'Planned Route System',
          'Emergency Mode'
        ]
      },
      specifications: [
        {
          label: { tr: 'GPS Hassasiyeti', en: 'GPS Accuracy' },
          value: { tr: '±2 metre', en: '±2 meters' }
        },
        {
          label: { tr: 'Çalışma Süresi', en: 'Operating Time' },
          value: { tr: '6-8 saat', en: '6-8 hours' }
        },
        {
          label: { tr: 'Kontrol Mesafesi', en: 'Control Range' },
          value: { tr: '500 metre', en: '500 meters' }
        },
        {
          label: { tr: 'Su Geçirmezlik', en: 'Waterproofing' },
          value: { tr: 'IPX8', en: 'IPX8' }
        },
        {
          label: { tr: 'Tekne Boyutu', en: 'Boat Size' },
          value: { tr: '8-15 metre', en: '8-15 meters' }
        },
        {
          label: { tr: 'Deniz Durumu', en: 'Sea Condition' },
          value: { tr: '4 deniz miline kadar', en: 'Up to sea state 4' }
        }
      ],
      useCases: {
        tr: [
          'Tekne Demirleme',
          'Balıkçı Tekneleri',
          'Yat ve Yelkenli Tekneler',
          'Arama Kurtarma',
          'Bilimsel Araştırma Tekneleri'
        ],
        en: [
          'Boat Anchoring',
          'Fishing Boats',
          'Yachts and Sailboats',
          'Search and Rescue',
          'Scientific Research Vessels'
        ]
      },
      technologies: ['GPS/GNSS', 'Marine Electronics', 'Wireless Control', 'Mobile App']
    }
  };

  const project = projects[slug as keyof typeof projects];

  if (!project) {
    return (
      <div className="py-20 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">{t.projectNotFound}</h1>
        <p className="text-gray-600 mb-8">{t.projectNotFoundDesc}</p>
        <Link to="/urunler">
          <Button>
            <ArrowLeft className="mr-2" size={16} />
            {t.backToProducts}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="py-12">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <Link to="/urunler" className="flex items-center text-primary hover:text-primary/90 transition-colors">
          <ArrowLeft className="mr-2" size={16} />
          {t.backToProducts}
        </Link>
      </div>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <Badge className="mb-4 bg-muted text-primary">
              {getLocalized(project.category)}
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
              {getLocalized(project.title)}
            </h1>
            <p className="text-lg text-muted leading-relaxed mb-8">
              {getLocalized(project.description)}
            </p>
            <div className="flex flex-col space-y-6">

              {slug === 'frigolive' && (
                <Link to="/downloads/frigolive">
                  <Button size="lg" className="group self-start">
                    {t.exploreMobileApp}
                  </Button>
                </Link>
              )}

              {slug === 'shalteron' && (
                <Link to="/downloads/shalteron">
                  <Button size="lg" className="group self-start">
                    {t.exploreMobileApp}
                  </Button>
                </Link>
              )}

              {slug === 'akilli-gecis-sistemi' && (
                <Link to="/downloads/akilli-gecis-sistemi">
                  <Button size="lg" className="group self-start">
                    {t.exploreMobileApp}
                  </Button>
                </Link>
              )}
            </div>
          </div>
          <div>
            {slug === 'shalteron' ? (
              <img
                src='/gsm_role.png'
                alt={getLocalized(project.title)}
                className="w-full h-auto rounded-lg shadow-xl"
              />
            ) : (
              <ImageWithFallback
                src={project.image}
                alt={getLocalized(project.title)}
                className="w-full h-auto rounded-lg shadow-xl"
              />
            )}
          </div>
        </div>
      </section>

      {/* Product Video (ShalterOn) */}
      {slug === 'shalteron' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <div className="flex justify-center">
            <div className="w-full max-w-3xl rounded-lg overflow-hidden shadow-xl bg-black">
              <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/35G4v2CRiKQ?rel=0"
                  title="ShalterOn Tanıtım Videosu"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                ></iframe>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ShalterOn İnteraktif Simülasyon */}
      {slug === 'shalteron' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-foreground mb-3">
              {language === 'tr' ? 'Canlı Demo' : 'Live Demo'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {language === 'tr'
                ? 'Soldaki telefon uygulamasından röleleri açıp kapatarak, sağdaki cihazların nasıl tepki verdiğini görün.'
                : 'See how the devices on the right respond by turning the relays on and off from the phone app on the left.'}
            </p>
          </div>

          <div className="shalteron-demo-wrapper flex flex-wrap justify-center items-start gap-12 md:gap-24 bg-muted/30 dark:bg-gray-800/50 p-6 md:p-10 rounded-2xl">
            {/* Sol Taraf: Telefon Simülasyonu */}
            <div className="phone-mockup w-[500px] md:w-[600px] bg-background border-[10px] border-gray-800 dark:border-gray-600 rounded-[2rem] p-6 shadow-2xl relative overflow-visible">
              {/* Telefon Notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-gray-800 dark:bg-gray-600 rounded-b-xl" />

              <div className="pt-4">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-border">
                  <span className="font-semibold text-foreground text-sm">shalteron Test Cihazı</span>
                </div>

                {/* Röle Kartları */}
                <div className="space-y-2">
                  {/* Röle 1 - Sera Aydınlatma */}
                  <div
                    className={`relay-card p-2.5 rounded-xl border-2 transition-all duration-300 ${relayStates.relay1 ? 'bg-green-50 dark:bg-green-900/30 border-green-500' : 'bg-background border-border'
                      }`}
                  >
                    <div className="flex flex-row items-center justify-between gap-3 w-full">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors flex-shrink-0 ${relayStates.relay1 ? 'bg-yellow-500 text-white' : 'bg-muted text-muted-foreground'
                            }`}
                        >
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M9 21c0 .5.4 1 1 1h4c.6 0 1-.5 1-1v-1H9v1zm3-19C8.1 2 5 5.1 5 9c0 2.4 1.2 4.5 3 5.7V17c0 .5.4 1 1 1h6c.6 0 1-.5 1-1v-2.3c1.8-1.3 3-3.4 3-5.7 0-3.9-3.1-7-7-7z" />
                          </svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-foreground text-xs truncate">
                            {language === 'tr' ? 'Sera Aydınlatma' : 'Greenhouse Lighting'}
                          </h4>
                          <p className={`text-[10px] ${relayStates.relay1 ? 'text-green-600' : 'text-muted-foreground'}`}>
                            ●{' '}
                            {relayStates.relay1
                              ? language === 'tr'
                                ? 'Aktif'
                                : 'Active'
                              : language === 'tr'
                                ? 'Pasif'
                                : 'Inactive'}
                          </p>
                        </div>
                      </div>
                      <div
                        className={`toggle-switch ${relayStates.relay1 ? 'active' : ''}`}
                        onClick={() => toggleRelay('relay1')}
                      >
                        <span className="toggle-icon"></span>
                      </div>
                    </div>
                  </div>

                  {/* Röle 2 - Su Pompası 1 */}
                  <div
                    className={`relay-card p-2.5 rounded-xl border-2 transition-all duration-300 ${relayStates.relay2 ? 'bg-green-50 dark:bg-green-900/30 border-green-500' : 'bg-background border-border'
                      }`}
                  >
                    <div className="flex flex-row items-center justify-between gap-3 w-full">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors flex-shrink-0 ${relayStates.relay2 ? 'bg-blue-500 text-white' : 'bg-muted text-muted-foreground'
                            }`}
                        >
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8zm0 18c-3.35 0-6-2.57-6-6.2 0-2.34 1.95-5.44 6-9.14 4.05 3.7 6 6.79 6 9.14 0 3.63-2.65 6.2-6 6.2z" />
                          </svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-foreground text-xs truncate">
                            {language === 'tr' ? 'Su Pompası 1' : 'Water Pump 1'}
                          </h4>
                          <p className={`text-[10px] ${relayStates.relay2 ? 'text-green-600' : 'text-muted-foreground'}`}>
                            ●{' '}
                            {relayStates.relay2
                              ? language === 'tr'
                                ? 'Aktif'
                                : 'Active'
                              : language === 'tr'
                                ? 'Pasif'
                                : 'Inactive'}
                          </p>
                        </div>
                      </div>
                      <div
                        className={`toggle-switch ${relayStates.relay2 ? 'active' : ''}`}
                        onClick={() => toggleRelay('relay2')}
                      >
                        <span className="toggle-icon"></span>
                      </div>
                    </div>
                  </div>

                  {/* Röle 3 - Su Pompası 2 */}
                  <div
                    className={`relay-card p-2.5 rounded-xl border-2 transition-all duration-300 ${relayStates.relay3 ? 'bg-green-50 dark:bg-green-900/30 border-green-500' : 'bg-background border-border'
                      }`}
                  >
                    <div className="flex flex-row items-center justify-between gap-3 w-full">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors flex-shrink-0 ${relayStates.relay3 ? 'bg-blue-500 text-white' : 'bg-muted text-muted-foreground'
                            }`}
                        >
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8zm0 18c-3.35 0-6-2.57-6-6.2 0-2.34 1.95-5.44 6-9.14 4.05 3.7 6 6.79 6 9.14 0 3.63-2.65 6.2-6 6.2z" />
                          </svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-foreground text-xs truncate">
                            {language === 'tr' ? 'Su Pompası 2' : 'Water Pump 2'}
                          </h4>
                          <p className={`text-[10px] ${relayStates.relay3 ? 'text-green-600' : 'text-muted-foreground'}`}>
                            ●{' '}
                            {relayStates.relay3
                              ? language === 'tr'
                                ? 'Aktif'
                                : 'Active'
                              : language === 'tr'
                                ? 'Pasif'
                                : 'Inactive'}
                          </p>
                        </div>
                      </div>
                      <div
                        className={`toggle-switch ${relayStates.relay3 ? 'active' : ''}`}
                        onClick={() => toggleRelay('relay3')}
                      >
                        <span className="toggle-icon"></span>
                      </div>
                    </div>
                  </div>

                  {/* Röle 4 - Su Pompası 3 */}
                  <div
                    className={`relay-card p-2.5 rounded-xl border-2 transition-all duration-300 ${relayStates.relay4 ? 'bg-green-50 dark:bg-green-900/30 border-green-500' : 'bg-background border-border'
                      }`}
                  >
                    <div className="flex flex-row items-center justify-between gap-3 w-full">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors flex-shrink-0 ${relayStates.relay4 ? 'bg-blue-500 text-white' : 'bg-muted text-muted-foreground'
                            }`}
                        >
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8zm0 18c-3.35 0-6-2.57-6-6.2 0-2.34 1.95-5.44 6-9.14 4.05 3.7 6 6.79 6 9.14 0 3.63-2.65 6.2-6 6.2z" />
                          </svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-foreground text-xs truncate">
                            {language === 'tr' ? 'Su Pompası 3' : 'Water Pump 3'}
                          </h4>
                          <p className={`text-[10px] ${relayStates.relay4 ? 'text-green-600' : 'text-muted-foreground'}`}>
                            ●{' '}
                            {relayStates.relay4
                              ? language === 'tr'
                                ? 'Aktif'
                                : 'Active'
                              : language === 'tr'
                                ? 'Pasif'
                                : 'Inactive'}
                          </p>
                        </div>
                      </div>
                      <div
                        className={`toggle-switch ${relayStates.relay4 ? 'active' : ''}`}
                        onClick={() => toggleRelay('relay4')}
                      >
                        <span className="toggle-icon"></span>
                      </div>
                    </div>
                  </div>

                  {/* Röle 5 - Havalandırma Fanı */}
                  <div
                    className={`relay-card p-2.5 rounded-xl border-2 transition-all duration-300 ${relayStates.relay5 ? 'bg-green-50 dark:bg-green-900/30 border-green-500' : 'bg-background border-border'
                      }`}
                  >
                    <div className="flex flex-row items-center justify-between gap-3 w-full">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors flex-shrink-0 ${relayStates.relay5 ? 'bg-red-500 text-white' : 'bg-muted text-muted-foreground'
                            }`}
                        >
                          <svg
                            className={`w-3.5 h-3.5 ${relayStates.relay5 ? 'animate-spin' : ''}`}
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M12 11c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1m2-8.95A8.61 8.61 0 0 1 12 2c-1.21 0-1.33 2.02-1.65 3.05-.11.34.1.71.42.86.2.09.42.09.6 0 .52-.26 1.19-.37 1.63-.37.44 0 1.11.11 1.63.37.18.09.4.09.6 0 .32-.15.53-.52.42-.86C15.33 4.02 15.21 2 14 2.05m4.95 8c-.55 0-1.05.25-1.38.64-.06.07-.06.17 0 .24.24.28.38.64.38 1.02 0 .88-.72 1.6-1.6 1.6a1.65 1.65 0 0 1-1.1-.43c-.07-.06-.17-.06-.24 0-.39.33-.64.83-.64 1.38 0 1.02.83 1.85 1.85 1.85.33 0 .64-.09.91-.24.07-.04.15-.04.22 0 .27.15.58.24.91.24 1.02 0 1.85-.83 1.85-1.85-.01-.56-.26-1.06-.65-1.39-.07-.06-.07-.16 0-.22.39-.34.64-.84.64-1.39 0-1.02-.83-1.85-1.85-1.85M9.05 14a1.65 1.65 0 0 1-1.1.43c-.88 0-1.6-.72-1.6-1.6 0-.38.14-.74.38-1.02.06-.07.06-.17 0-.24-.33-.39-.83-.64-1.38-.64-1.02 0-1.85.83-1.85 1.85 0 .55.25 1.05.64 1.39.07.06.07.16 0 .22-.39.33-.64.83-.64 1.39 0 1.02.83 1.85 1.85 1.85.33 0 .64-.09.91-.24.07-.04.15-.04.22 0 .27.15.58.24.91.24 1.02 0 1.85-.83 1.85-1.85 0-.55-.25-1.05-.64-1.38-.07-.07-.17-.07-.24 0M12 19.55c-.44 0-1.11-.11-1.63-.37-.18-.09-.4-.09-.6 0-.32.15-.53.52-.42.86.32 1.03.44 3.05 1.65 3.05 1.21 0 1.33-2.02 1.65-3.05.11-.34-.1-.71-.42-.86-.2-.09-.42-.09-.6 0-.52.26-1.19.37-1.63.37" />
                          </svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-foreground text-xs truncate">
                            {language === 'tr' ? 'Havalandırma Fanı' : 'Ventilation Fan'}
                          </h4>
                          <p className={`text-[10px] ${relayStates.relay5 ? 'text-green-600' : 'text-muted-foreground'}`}>
                            ●{' '}
                            {relayStates.relay5
                              ? language === 'tr'
                                ? 'Aktif'
                                : 'Active'
                              : language === 'tr'
                                ? 'Pasif'
                                : 'Inactive'}
                          </p>
                        </div>
                      </div>
                      <div
                        className={`toggle-switch ${relayStates.relay5 ? 'active' : ''}`}
                        onClick={() => toggleRelay('relay5')}
                      >
                        <span className="toggle-icon"></span>
                      </div>
                    </div>
                  </div>

                  {/* Röle 6 - Elektrikli Kapı */}
                  <div
                    className={`relay-card p-2.5 rounded-xl border-2 transition-all duration-300 ${relayStates.relay6 ? 'bg-green-50 dark:bg-green-900/30 border-green-500' : 'bg-background border-border'
                      }`}
                  >
                    <div className="flex flex-row items-center justify-between gap-3 w-full">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors flex-shrink-0 ${relayStates.relay6 ? 'bg-purple-500 text-white' : 'bg-muted text-muted-foreground'
                            }`}
                        >
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M19 19V5c0-1.1-.9-2-2-2H7c-1.1 0-2 .9-2 2v14H3v2h18v-2h-2zm-8 0H9V5h2v14zm4 0h-2V5h2v14z" />
                          </svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-foreground text-xs truncate">
                            {language === 'tr' ? 'Elektrikli Kapı' : 'Electric Gate'}
                          </h4>
                          <p className={`text-[10px] ${relayStates.relay6 ? 'text-green-600' : 'text-muted-foreground'}`}>
                            ●{' '}
                            {relayStates.relay6
                              ? language === 'tr'
                                ? 'Aktif'
                                : 'Active'
                              : language === 'tr'
                                ? 'Pasif'
                                : 'Inactive'}
                          </p>
                        </div>
                      </div>
                      <div
                        className={`toggle-switch ${relayStates.relay6 ? 'active' : ''}`}
                        onClick={() => toggleRelay('relay6')}
                      >
                        <span className="toggle-icon"></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sağ Taraf: Fiziksel Cihazlar */}
            <div className="devices-grid grid grid-cols-2 gap-4 w-[280px] md:w-[320px]">
              {/* Cihaz 1 - Sera Işıkları */}
              <div
                className={`device-box rounded-2xl p-4 flex flex-col items-center justify-center h-[130px] border-2 transition-all duration-500 ${relayStates.relay1
                  ? 'border-yellow-500 shadow-lg shadow-yellow-200 dark:shadow-yellow-900/30 opacity-100'
                  : 'border-border opacity-50 grayscale'
                  }`}
                style={{ backgroundColor: 'rgba(255, 255, 255, 0)' }}
              >
                <div
                  className={`text-3xl mb-2 transition-all duration-300 ${relayStates.relay1 ? 'drop-shadow-[0_0_15px_rgba(234,179,8,0.8)]' : ''
                    }`}
                >
                  💡
                </div>
                <div className="font-semibold text-foreground text-xs text-center">
                  {language === 'tr' ? 'Sera Işıkları' : 'Greenhouse Lights'}
                </div>
                <div
                  className={`text-[10px] mt-1 font-medium ${relayStates.relay1 ? 'text-green-600' : 'text-muted-foreground'
                    }`}
                >
                  {relayStates.relay1
                    ? language === 'tr'
                      ? 'Açık'
                      : 'On'
                    : language === 'tr'
                      ? 'Kapalı'
                      : 'Off'}
                </div>
              </div>

              {/* Cihaz 2 - Su Pompası 1 */}
              <div
                className={`device-box rounded-2xl p-4 flex flex-col items-center justify-center h-[130px] border-2 transition-all duration-500 ${relayStates.relay2
                  ? 'border-blue-500 shadow-lg shadow-blue-200 dark:shadow-blue-900/30 opacity-100'
                  : 'border-border opacity-50 grayscale'
                  }`}
                style={{ backgroundColor: 'rgba(255, 255, 255, 0)' }}
              >
                <div
                  className={`text-3xl mb-2 transition-all duration-300 ${relayStates.relay2 ? 'animate-bounce' : ''
                    }`}
                >
                  💧
                </div>
                <div className="font-semibold text-foreground text-xs text-center">
                  {language === 'tr' ? 'Su Pompası 1' : 'Water Pump 1'}
                </div>
                <div
                  className={`text-[10px] mt-1 font-medium ${relayStates.relay2 ? 'text-green-600' : 'text-muted-foreground'
                    }`}
                >
                  {relayStates.relay2
                    ? language === 'tr'
                      ? 'Açık'
                      : 'On'
                    : language === 'tr'
                      ? 'Kapalı'
                      : 'Off'}
                </div>
              </div>

              {/* Cihaz 3 - Su Pompası 2 */}
              <div
                className={`device-box rounded-2xl p-4 flex flex-col items-center justify-center h-[130px] border-2 transition-all duration-500 ${relayStates.relay3
                  ? 'border-blue-500 shadow-lg shadow-blue-200 dark:shadow-blue-900/30 opacity-100'
                  : 'border-border opacity-50 grayscale'
                  }`}
                style={{ backgroundColor: 'rgba(255, 255, 255, 0)' }}
              >
                <div
                  className={`text-3xl mb-2 transition-all duration-300 ${relayStates.relay3 ? 'animate-bounce' : ''
                    }`}
                >
                  💧
                </div>
                <div className="font-semibold text-foreground text-xs text-center">
                  {language === 'tr' ? 'Su Pompası 2' : 'Water Pump 2'}
                </div>
                <div
                  className={`text-[10px] mt-1 font-medium ${relayStates.relay3 ? 'text-green-600' : 'text-muted-foreground'
                    }`}
                >
                  {relayStates.relay3
                    ? language === 'tr'
                      ? 'Açık'
                      : 'On'
                    : language === 'tr'
                      ? 'Kapalı'
                      : 'Off'}
                </div>
              </div>

              {/* Cihaz 4 - Su Pompası 3 */}
              <div
                className={`device-box rounded-2xl p-4 flex flex-col items-center justify-center h-[130px] border-2 transition-all duration-500 ${relayStates.relay4
                  ? 'border-blue-500 shadow-lg shadow-blue-200 dark:shadow-blue-900/30 opacity-100'
                  : 'border-border opacity-50 grayscale'
                  }`}
                style={{ backgroundColor: 'rgba(255, 255, 255, 0)' }}
              >
                <div
                  className={`text-3xl mb-2 transition-all duration-300 ${relayStates.relay4 ? 'animate-bounce' : ''
                    }`}
                >
                  💧
                </div>
                <div className="font-semibold text-foreground text-xs text-center">
                  {language === 'tr' ? 'Su Pompası 3' : 'Water Pump 3'}
                </div>
                <div
                  className={`text-[10px] mt-1 font-medium ${relayStates.relay4 ? 'text-green-600' : 'text-muted-foreground'
                    }`}
                >
                  {relayStates.relay4
                    ? language === 'tr'
                      ? 'Açık'
                      : 'On'
                    : language === 'tr'
                      ? 'Kapalı'
                      : 'Off'}
                </div>
              </div>

              {/* Cihaz 5 - Fanlar */}
              <div
                className={`device-box rounded-2xl p-4 flex flex-col items-center justify-center h-[130px] border-2 transition-all duration-500 ${relayStates.relay5
                  ? 'border-red-500 shadow-lg shadow-red-200 dark:shadow-red-900/30 opacity-100'
                  : 'border-border opacity-50 grayscale'
                  }`}
                style={{ backgroundColor: 'rgba(255, 255, 255, 0)' }}
              >
                <div
                  className={`text-3xl mb-2 transition-all duration-300 ${relayStates.relay5 ? 'animate-spin' : ''
                    }`}
                  style={{ animationDuration: '1s' }}
                >
                  🌀
                </div>
                <div className="font-semibold text-foreground text-xs text-center">
                  {language === 'tr' ? 'Fanlar' : 'Fans'}
                </div>
                <div
                  className={`text-[10px] mt-1 font-medium ${relayStates.relay5 ? 'text-green-600' : 'text-muted-foreground'
                    }`}
                >
                  {relayStates.relay5
                    ? language === 'tr'
                      ? 'Açık'
                      : 'On'
                    : language === 'tr'
                      ? 'Kapalı'
                      : 'Off'}
                </div>
              </div>

              {/* Cihaz 6 - Depo Kapısı */}
              <div
                className={`device-box rounded-2xl p-4 flex flex-col items-center justify-center h-[130px] border-2 transition-all duration-500 ${relayStates.relay6
                  ? 'border-purple-500 shadow-lg shadow-purple-200 dark:shadow-purple-900/30 opacity-100'
                  : 'border-border opacity-50 grayscale'
                  }`}
                style={{ backgroundColor: 'rgba(255, 255, 255, 0)' }}
              >
                <div
                  className={`text-3xl mb-2 transition-all duration-300 ${relayStates.relay6 ? 'scale-110' : ''
                    }`}
                >
                  🚪
                </div>
                <div className="font-semibold text-foreground text-xs text-center">
                  {language === 'tr' ? 'Depo Kapısı' : 'Warehouse Door'}
                </div>
                <div
                  className={`text-[10px] mt-1 font-medium ${relayStates.relay6 ? 'text-green-600' : 'text-muted-foreground'
                    }`}
                >
                  {relayStates.relay6
                    ? language === 'tr'
                      ? 'Açık'
                      : 'On'
                    : language === 'tr'
                      ? 'Kapalı'
                      : 'Off'}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <h2 className="text-3xl font-bold text-foreground mb-6">{t.features}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {getLocalized(project.features).map((feature, index) => (
            <div
              key={index}
              className="flex items-center !bg-white dark:!bg-white p-3 rounded-lg border text-sm"
            >
              <CheckCircle className="text-primary mr-2 flex-shrink-0" size={18} />
              <span className="!text-muted dark:!text-white font-medium leading-snug">{feature}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ShalterOn - Akıllı Takip ve Alarm Sistemi (Digital Inputs) */}
      {slug === 'shalteron' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-foreground mb-3">
              {language === 'tr' ? 'Akıllı Takip ve Alarm Sistemi' : 'Smart Tracking and Alarm System'}
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              {language === 'tr'
                ? 'ShalterOn sadece sizin komutlarınızı uygulamaz, aynı zamanda sahadaki durumu size bildirir. 4 Adet Dijital Giriş portu sayesinde sensörlerden gelen verileri anlık olarak izleyin.'
                : 'ShalterOn not only executes your commands, but also informs you of the situation in the field. Monitor data from sensors instantly with 4 Digital Input ports.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Hata Bildirimi */}
            <Card className="border-2 border-red-200 dark:border-red-800 bg-gradient-to-br from-red-50 to-white dark:from-red-900/20 dark:to-background">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/50 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground mb-2">
                      {language === 'tr' ? 'Hata Bildirimi' : 'Error Notification'}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {language === 'tr'
                        ? 'Motor termik rölesi attı, uygulama anında kırmızı alarm verir. Arıza anında anında haberdar olun.'
                        : 'If the motor thermal relay trips, the app gives a red alarm instantly. Be notified immediately in case of malfunction.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Su Seviyesi */}
            <Card className="border-2 border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-background">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8zm0 18c-3.35 0-6-2.57-6-6.2 0-2.34 1.95-5.44 6-9.14 4.05 3.7 6 6.79 6 9.14 0 3.63-2.65 6.2-6 6.2z" /></svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground mb-2">
                      {language === 'tr' ? 'Su Seviyesi Takibi' : 'Water Level Tracking'}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {language === 'tr'
                        ? 'Depo dolduğunda şamandıra sensörü size "Dolu" bilgisini gönderir. Su taşmalarını önleyin.'
                        : 'When the tank is full, the float sensor sends you a "Full" notification. Prevent water overflows.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* İzinsiz Giriş */}
            <Card className="border-2 border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/20 dark:to-background">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground mb-2">
                      {language === 'tr' ? 'İzinsiz Giriş Alarmı' : 'Unauthorized Entry Alarm'}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {language === 'tr'
                        ? 'Depo kapısı açıldığında manyetik kontak durumu anında telefonunuza iletilir. Güvenliğiniz her an kontrol altında.'
                        : 'When the warehouse door is opened, the magnetic contact status is instantly sent to your phone. Your security is always under control.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Acil Durum Simülasyonu - Sadece Akıllı Geçiş Sistemi için */}
      {slug === 'akilli-gecis-sistemi' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <div className="bg-gradient-to-r from-red-50 to-red-50 dark:from-red-900/20 dark:to-red-900/20 rounded-xl p-8 border-2 border-dashed transition-all duration-500" style={{ borderColor: emergencyMode ? '#d4183d' : '#ef4444' }}>
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-3 mb-3">
                  <AlertTriangle className={`w-8 h-8 transition-colors duration-300 ${emergencyMode ? 'text-red-600' : 'text-red-600'}`} />
                  <h3 className="text-2xl font-bold text-foreground">
                    {emergencyMode ? t.emergencyModeActive : 'Acil Durum Simülasyonu'}
                  </h3>
                </div>
                <p className="text-muted-foreground">
                  {emergencyMode ? t.emergencyModeDesc : 'Yangın veya acil durum anında sistemin nasıl çalıştığını test edin. Tüm kapılar otomatik olarak açılır.'}
                </p>
              </div>
              <Button
                variant={emergencyMode ? "default" : "destructive"}
                size="lg"
                onClick={() => setEmergencyMode(!emergencyMode)}
                className={`font-bold transition-all shadow-lg hover:scale-105 ${emergencyMode ? 'emergency-button-active !text-white' : ''}`}
              >
                {emergencyMode ? (
                  <>
                    <CheckCircle className="mr-2 w-5 h-5 !text-white" style={{ color: '#ffffff' }} />
                    <span className="!text-white" style={{ color: '#ffffff' }}>{t.emergencyModeActive}</span>
                  </>
                ) : (
                  <>
                    <PlayCircle className="mr-2 w-5 h-5" />
                    {t.testEmergencyMode}
                  </>
                )}
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Strategic Highlights (Akıllı Geçiş Sistemi) */}
      {slug === 'akilli-gecis-sistemi' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16 scroll-animate" data-animation="animate-fade-in-up">
          <h2 className="text-3xl font-bold text-foreground mb-8">Güvenlik, Süreklilik ve Uzaktan Kontrol</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="h-full border shadow-sm">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex items-center gap-2 mb-3 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-semibold">Acil Durum Senaryoları ve Güvenlik Entegrasyonu</span>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  Kurumsal güvenlik politikalarının en kritik bileşeni olan acil durum tahliyesi, İlimera AGS ile risk faktörlerinden arındırılmıştır.
                  Yangın veya deprem sensörlerinden alınan sinyallerle eş zamanlı çalışan sistemimiz, manuel müdahaleye gerek kalmaksızın tüm bariyer ve kilit mekanizmalarını otomatik olarak devre dışı bırakır.
                  Bu proaktif yaklaşım, tahliye süreçlerindeki bariyer kaynaklı riskleri minimize ederek mevzuatlara tam uyum sağlar.
                </p>
              </CardContent>
            </Card>

            <Card className="h-full border shadow-sm">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex items-center gap-2 mb-3 text-foreground">
                  <CloudOff className="w-5 h-5 text-foreground" />
                  <span className="font-semibold">Kesintisiz Operasyon ve Çevrimdışı Çalışma</span>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  Kurumunuzun güvenliği internet bağlantısına bağımlı değildir. İlimera AGS, geliştirdiğimiz hibrit çalışma teknolojisi sayesinde, ağ kesintilerinde dahi yerel belleğini kullanarak yetkilendirme ve denetim faaliyetlerini kesintisiz sürdürür.
                  Bağlantı sağlandığında gerçekleşen otomatik senkronizasyon ile hiçbir veri kaybı yaşanmaz; güvenlik ve raporlama süreçleriniz %100 süreklilik arz eder.
                </p>
              </CardContent>
            </Card>

            <Card className="h-full border shadow-sm">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex items-center gap-2 mb-3 text-emerald-600">
                  <Lock className="w-5 h-5" />
                  <span className="font-semibold">Uzaktan Erişim ve Merkezi Denetim</span>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  Mekan ve zamandan bağımsız yönetim imkanı sunan bulut altyapımız, tam kontrolü parmaklarınızın ucuna getirir.
                  Yetkili yöneticiler, web arayüzü veya mobil uygulama üzerinden tüm geçiş noktalarını izleyebilir, anlık müdahalelerde bulunabilir ve yetki matrislerini güncelleyebilirler.
                  Bu yapı, çok şubeli veya geniş yerleşkeli kurumların tek merkezden, efektif ve güvenli bir şekilde yönetilmesini sağlar.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Technical Specifications */}
      {slug !== 'akilli-gecis-sistemi' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-3xl font-bold text-foreground mb-8">{t.technicalSpecs}</h2>
          <Card>
            <CardContent className="p-6">
              {slug === 'frigolive' ? (
                <div className="space-y-6">
                  {/* Bağlantı */}
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">{t.connection}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {project.specifications.slice(0, 1).map((spec, index) => (
                        <div key={index} className="flex justify-between items-center py-1 border-b border-gray-100">
                          <span className="font-medium text-primary">{getLocalized(spec.label)}:</span>
                          <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Konum */}
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">{t.location}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {project.specifications.slice(1, 3).map((spec, index) => (
                        <div key={index} className="flex justify-between items-center py-1 border-b border-gray-100">
                          <span className="font-medium text-primary">{getLocalized(spec.label)}:</span>
                          <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Sensörler */}
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">{t.sensors}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {project.specifications.slice(3, 6).map((spec, index) => (
                        <div key={index} className="flex justify-between items-center py-1 border-b border-gray-100">
                          <span className="font-medium text-primary">{getLocalized(spec.label)}:</span>
                          <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Sıcaklık ve Nem */}
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">{t.temperatureHumidity}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {project.specifications.slice(6, 13).map((spec, index) => (
                        <div key={index} className="flex justify-between items-center py-1 border-b border-gray-100">
                          <span className="font-medium text-primary">{getLocalized(spec.label)}:</span>
                          <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Batarya */}
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">{t.battery}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {project.specifications.slice(13, 15).map((spec, index) => (
                        <div key={index} className="flex justify-between items-center py-1 border-b border-gray-100">
                          <span className="font-medium text-primary">{getLocalized(spec.label)}:</span>
                          <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Genel */}
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">{t.general}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {project.specifications.slice(15).map((spec, index) => (
                        <div key={index} className="flex justify-between items-center py-1 border-b border-gray-100">
                          <span className="font-medium text-primary">{getLocalized(spec.label)}:</span>
                          <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {project.specifications.map((spec, index) => (
                    <div key={index} className="flex justify-between items-center py-3 border-b border-gray-100">
                      <span className="font-medium text-primary">{getLocalized(spec.label)}:</span>
                      <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </section>
      )}

      {/* Package Contents - ShalterOn */}
      {slug === 'shalteron' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-3xl font-bold text-foreground mb-8">{t.packageContents}</h2>
          <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-center p-4 bg-white/50 dark:bg-gray-800/50 rounded-lg border border-primary/20">
                  <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center mr-4">
                    <Settings className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">1 {t.quantity}</p>
                    <p className="text-muted-foreground">{t.packageItem1}</p>
                  </div>
                </div>
                <div className="flex items-center p-4 bg-white/50 dark:bg-gray-800/50 rounded-lg border border-primary/20">
                  <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center mr-4">
                    <Wifi className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">1 {t.quantity}</p>
                    <p className="text-muted-foreground">{t.packageItem2}</p>
                  </div>
                </div>
                <div className="flex items-center p-4 bg-white/50 dark:bg-gray-800/50 rounded-lg border border-primary/20">
                  <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">1 {t.quantity}</p>
                    <p className="text-muted-foreground">{t.packageItem3}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Warnings */}
      {project.warnings && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-3xl font-bold text-foreground mb-8">{t.warningsTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {getLocalized(project.warnings).map((warning, index) => (
              <Card key={index} className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/20">
                <CardContent className="p-4">
                  <div className="flex items-start">
                    <div className="w-6 h-6 rounded-full mt-0.5 mr-3 flex-shrink-0 flex items-center justify-center shadow-md border border-orange-500">
                      <svg className="w-4 h-4 text-orange-500 animate-pulse" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-orange-800 dark:text-orange-200 font-medium">{warning}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Use Cases */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <h2 className="text-3xl font-bold text-foreground mb-8">{t.useCases}</h2>
        {slug === 'akilli-gecis-sistemi' && project.detailedUseCases ? (
          <div className="space-y-6">
            {project.detailedUseCases.map((item, idx) => (
              <Card key={idx} className="border">
                <CardContent className="p-6">
                  {idx === 0 ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                            <Settings className="text-primary" size={20} />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">{getLocalized(item.title)}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          {item.points && getLocalized(item.points).map((point, pointIdx) => (
                            <li key={pointIdx}>{point}</li>
                          ))}
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className="group overflow-hidden rounded-md block md:hidden">
                        <img
                          src="/ticket-barriers-subway-entrance.jpg"
                          alt="Turnike Geçiş Sistemleri"
                          className="block w-full h-auto object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                      {/* Desktop image */}
                      <div className="hidden md:block group overflow-hidden rounded-md" style={{ width: 533, height: 360 }}>
                        <img
                          src="/ticket-barriers-subway-entrance.jpg"
                          alt="Turnike Geçiş Sistemleri"
                          className="block w-full h-full object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                    </div>
                  ) : idx === 1 ? (
                    <div className={`flex flex-col md:flex-row md:items-center gap-6 transition-all duration-500 ${emergencyMode ? 'door-emergency-active rounded-lg p-2' : ''}`}>
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className={`w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4 transition-colors duration-300 ${emergencyMode ? 'bg-green-100 dark:bg-green-900' : ''}`}>
                            <Settings className={`transition-colors duration-300 ${emergencyMode ? 'text-green-600' : 'text-primary'}`} size={20} />
                          </div>
                          <h3 className={`text-xl font-semibold text-foreground transition-colors duration-300 ${emergencyMode ? 'text-green-600' : ''}`}>{getLocalized(item.title)}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          {item.points && getLocalized(item.points).map((point, pointIdx) => (
                            <li key={pointIdx}>{point}</li>
                          ))}
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className={`group overflow-hidden rounded-md block md:hidden transition-all duration-500 ${emergencyMode ? 'door-emergency-active' : ''}`}>
                        <img
                          src="/21228.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className={`block w-full h-auto object-contain transform transition-all duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in ${emergencyMode ? 'door-emergency-active' : ''}`}
                        />
                      </div>
                      {/* Desktop image */}
                      <div className={`hidden md:block group overflow-hidden rounded-md transition-all duration-500 ${emergencyMode ? 'door-emergency-active' : ''}`} style={{ width: 533, height: 400 }}>
                        <img
                          src="/21228.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className={`block w-full h-full object-contain transform transition-all duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in ${emergencyMode ? 'door-emergency-active' : ''}`}
                        />
                      </div>
                    </div>
                  ) : idx === 2 ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                            <Settings className="text-primary" size={20} />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">{getLocalized(item.title)}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          {item.points && getLocalized(item.points).map((point, pointIdx) => (
                            <li key={pointIdx}>{point}</li>
                          ))}
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className="group overflow-hidden rounded-md block md:hidden">
                        <img
                          src="/chef-preparing-recipe.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-auto object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                      {/* Desktop image */}
                      <div className="hidden md:block group overflow-hidden rounded-md" style={{ width: 533, height: 400 }}>
                        <img
                          src="/chef-preparing-recipe.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-full object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                    </div>
                  ) : idx === 3 ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                            <Settings className="text-primary" size={20} />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">{getLocalized(item.title)}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          {item.points && getLocalized(item.points).map((point, pointIdx) => (
                            <li key={pointIdx}>{point}</li>
                          ))}
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className="group overflow-hidden rounded-md block md:hidden">
                        <img
                          src="/empty-hallway-background.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-auto object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                      {/* Desktop image */}
                      <div className="hidden md:block group overflow-hidden rounded-md" style={{ width: 533, height: 400 }}>
                        <img
                          src="/empty-hallway-background.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-full object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                    </div>
                  ) : idx === 4 ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                            <Settings className="text-primary" size={20} />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">{getLocalized(item.title)}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          {item.points && getLocalized(item.points).map((point, pointIdx) => (
                            <li key={pointIdx}>{point}</li>
                          ))}
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className="group overflow-hidden rounded-md block md:hidden">
                        <img
                          src="/young-student-learning-library.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-auto object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                      {/* Desktop image */}
                      <div className="hidden md:block group overflow-hidden rounded-md" style={{ width: 533, height: 400 }}>
                        <img
                          src="/young-student-learning-library.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-full object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                    </div>
                  ) : idx === 5 ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                            <Settings className="text-primary" size={20} />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">{getLocalized(item.title)}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          {item.points && getLocalized(item.points).map((point, pointIdx) => (
                            <li key={pointIdx}>{point}</li>
                          ))}
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className="group overflow-hidden rounded-md block md:hidden">
                        <img
                          src="/retro-theatre-scene-celebrate-world-theatre-day.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-auto object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                      {/* Desktop image */}
                      <div className="hidden md:block group overflow-hidden rounded-md" style={{ width: 533, height: 400 }}>
                        <img
                          src="/retro-theatre-scene-celebrate-world-theatre-day.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-full object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                    </div>
                  ) : idx === 6 ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                            <Settings className="text-primary" size={20} />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">{getLocalized(item.title)}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          {item.points && getLocalized(item.points).map((point, pointIdx) => (
                            <li key={pointIdx}>{point}</li>
                          ))}
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className="group overflow-hidden rounded-md block md:hidden">
                        <img
                          src="/abstract-blur-shopping-mall-retails-store-interior-background.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-auto object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110"
                        />
                      </div>
                      {/* Desktop image */}
                      <div className="hidden md:block group overflow-hidden rounded-md" style={{ width: 533, height: 400 }}>
                        <img
                          src="/abstract-blur-shopping-mall-retails-store-interior-background.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-full object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110"
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start mb-4">
                        <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                          <Settings className="text-primary" size={20} />
                        </div>
                        <h3 className="text-xl font-semibold text-foreground">{getLocalized(item.title)}</h3>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">{getLocalized(item.description)}</p>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {getLocalized(project.useCases).map((useCase, index) => (
              <Card key={index} className="p-6">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                    <Settings className="text-primary" size={20} />
                  </div>
                  <span className="text-foreground font-medium">{useCase}</span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Product Documentation (ShalterOn) */}
      {slug === 'shalteron' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-8">{t.productDocumentation}</h2>
          <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
            <CardContent className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground">ShalterOn {language === 'tr' ? 'Kullanım Kılavuzu' : 'User Manual'}</h3>
                    <p className="text-muted-foreground">{language === 'tr' ? 'Detaylı kurulum ve kullanım bilgileri' : 'Detailed installation and usage information'}</p>
                  </div>
                </div>
                <Button
                  onClick={() => window.open('/ShalterOn_v1.5.pdf', '_blank', 'noopener,noreferrer')}
                  className="bg-primary hover:bg-primary/90 text-white"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {t.viewPdf}
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Technologies */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <h2 className="text-3xl font-bold text-foreground mb-8">{t.technologies}</h2>
        <div className="flex flex-wrap gap-3">
          {project.technologies.map((tech) => (
            <Badge key={tech} variant="outline" className="px-4 py-2 text-sm">
              {tech}
            </Badge>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            {t.ctaTitle}
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            {t.ctaDescription}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {slug === 'frigolive' && (
              <Button
                size="lg"
                className="group self-start"
                onClick={() => window.open('https://frigolive.com/', '_blank', 'noopener,noreferrer')}
              >
                {t.visitWebsite}
              </Button>
            )}

            <Link to="/iletisim">
              <Button
                size="lg"
                variant="outline"
                className="dark:bg-gray-800 border-primary text-primary dark:hover:bg-gray-800 hover:bg-primary dark:hover:text-white"
              >
                {t.contactUs}
              </Button>
            </Link>

            <Link to="/urunler">
              <Button
                variant="outline"
                size="lg"
                className="dark:bg-gray-800 border-primary text-primary dark:hover:bg-gray-800 hover:bg-primary dark:hover:text-white">
                {t.viewOtherProjects}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}